"use client";

import { getOrSetCart } from "@/app/actions/cart";
import { wishlistApi } from "@/app/lib/wishlistApi";
import { getCookieData } from "@/app/utils/cookiehandler";
import { trackAddToWishlist, trackRemoveFromWishlist } from "@/app/utils/gtm-events/contact";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
} from "react";

interface User {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  addresses: any[];
  orders: any[];
  // wishlist?: number[];
  wishlist?: any[];
  wishlistItemCount?: number;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;

  login: (email: string, password: string) => Promise<void>;
  signup: (data: {
    name: string;
    email: string;
    password: string;
    phoneNo: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profileData: {
    firstName: string;
    lastName: string;
    mobile: string;
  }) => Promise<boolean>;
  refreshUser: () => Promise<void>;
  scrollToProducts?: () => void; // optional
  // Wishlist
  wishlistSet: Set<number>;
  addToWishlist: (productId: number) => void;
  removeFromWishlist: (productId: number) => void;
  isInWishlist: (productId: number) => boolean;
  // Wishlist (PDP only)
  toggleWishlist: (productId: number, variantId: number, productTitle: string, productPrice: number) => Promise<boolean>;
  wishlistCount: number;

  // Updated: isWishlisted is now read-only, toggle function handles it
  isWishlisted: boolean;
  setIsWishlisted: React.Dispatch<React.SetStateAction<boolean>>;
  setWishlistCount: React.Dispatch<React.SetStateAction<any>>;
  showAuthModal: boolean;
  setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>;

  productListingRef: any;
}

/* ---------------- CONTEXT ---------------- */

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wishlistSet, setWishlistSet] = useState<Set<number>>(new Set());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0); // Tracking wishlist count

  const productListingRef = useRef<HTMLDivElement>(null);

  // Scroll function (optional chaining makes it safe everywhere)
  const scrollToProducts = () => {
    const productListingRef = document.getElementById("product-listing"); // or use ref
    productListingRef?.scrollIntoView({ behavior: "smooth" });
  };

  const [isWishlisted, setIsWishlisted] = useState(false);

  // Keep track of the current product being checked for wishlist status
  const currentProductRef = useRef<{
    productId: number;
    variantId: number;
  } | null>(null);

  /* ---------------- USER FETCH ---------------- */

const fetchUser = async () => {
  // Check if the _aicodepro_jwt cookie is present using js-cookie
  const jwtCookie = getCookieData('_aicodepro_jwt');
  
  // If the cookie is not present, return early
  if (!jwtCookie) {
    setUser(null);
    setIsLoggedIn(false);
    setWishlistCount(0);
    setIsWishlisted(false);
    return;
  }

  try {
    // If the cookie is present, fetch user data from the API
    const res = await fetch("/api/user", {
      credentials: "include",
      cache: "no-store",
    });

    if (!res.ok) {
      setUser(null);
      setIsLoggedIn(false);
      setWishlistCount(0);
      setIsWishlisted(false);
      return;
    }

    const data = await res.json();
    setUser(data);
    setIsLoggedIn(true);

    setWishlistCount(data?.wishlistItemCount || 0);

    // Check if current product is in wishlist
    if (currentProductRef.current) {
      await checkCurrentProductWishlistStatus();
    }

    await getOrSetCart();
  } catch {
    setUser(null);
    setIsLoggedIn(false);
    setWishlistCount(0);
    setIsWishlisted(false);
  }
};

  // Function to check wishlist status for current product
  const checkCurrentProductWishlistStatus = async () => {
    if (!currentProductRef.current || !isLoggedIn) {
      setIsWishlisted(false);
      return;
    }

    try {
      const { productId, variantId } = currentProductRef.current;
      const checkRes = await wishlistApi.checkItemInWishlist(
        productId,
        variantId
      );
      setIsWishlisted(!!checkRes?.data?.wishlistItem?.id);
    } catch (error) {
      console.error("[WISHLIST] Failed to check wishlist status", error);
      setIsWishlisted(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const refreshUser = async () => {
    await fetchUser();
  };

  // --- ✅ Signup ---
  const signup = async (data: {
    name: string;
    email: string;
    password: string;
    phoneNo: string;
  }) => {
    const res = await fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });

    const result = await res.json();

    if (!res.ok || !result.success) {
      throw new Error(result.message || "Signup failed");
    }

    await refreshUser();
  };

  // --- ✅ Login ---
  /* ---------------- UPDATED WISHLIST TOGGLE FUNCTION ---------------- */

  const toggleWishlist = async (
    productId: number,
    variantId: number,
    productTitle: string,
    productPrice: number
  ): Promise<boolean> => {
    // Set current product reference for wishlist status tracking
    currentProductRef.current = { productId, variantId };

    if (!isLoggedIn) {
      setShowAuthModal(true);
      return false;
    }

    try {
      // 1️⃣ Check from server
      const checkRes = await wishlistApi.checkItemInWishlist(
        productId,
        variantId
      );

      const wishlistItemId = checkRes?.data?.wishlistItem?.id;
      let isAdding = false;

      if (wishlistItemId) {
        // 2️⃣ Remove - decrement count immediately and set isWishlisted to false
        setWishlistCount((prev) => Math.max(0, prev - 1));
        setIsWishlisted(false); // Set to false on remove
        await wishlistApi.removeFromWishlist(wishlistItemId);
        trackRemoveFromWishlist({
          id: productId,
          name: productTitle,
        });
      } else {
        // 3️⃣ Add - increment count immediately and set isWishlisted to true
        setWishlistCount((prev) => prev + 1);
        setIsWishlisted(true); // Set to true on add
        isAdding = true;
        await wishlistApi.addToWishlist(productId, variantId);
        trackAddToWishlist({
          id: productId,
          name: productTitle,
          price: productPrice ?? 0,
          currency: "INR",
        });
      }

      // 4️⃣ Sync user state
      try {
        await refreshUser();
      } catch (error) {
        console.error(
          "[WISHLIST] Refresh failed, but toggle succeeded locally",
          error
        );

        // If refresh fails, revert all changes
        if (isAdding) {
          setWishlistCount((prev) => Math.max(0, prev - 1));
          setIsWishlisted(false); // Revert to false if add failed
        } else {
          setWishlistCount((prev) => prev + 1);
          setIsWishlisted(true); // Revert to true if remove failed
        }
        throw error;
      }

      return true;
    } catch (error) {
      console.error("[WISHLIST] Toggle failed", error);
      return false;
    }
  };

  /* ---------------- AUTH ACTIONS ---------------- */

  const login = async (email: string, password: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!data.success) throw new Error(data.message || "Login failed");

    await refreshUser();
  };

  // --- ✅ Logout ---
  const logout = async () => {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });

    setUser(null);
    setIsLoggedIn(false);
    setWishlistCount(0);
    setIsWishlisted(false);
    currentProductRef.current = null;
  };

  // --- ✅ Update Profile ---
  const updateProfile = async (profileData: {
    firstName: string;
    lastName: string;
    mobile: string;
  }): Promise<boolean> => {
    try {
      const res = await fetch("/api/user", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileData),
      });

      const data = await res.json();
      if (!res.ok || data?.success === false) {
        console.error("Profile update failed:", data?.message);
        return false;
      }

      await refreshUser();
      return true;
    } catch (error) {
      console.error("Profile update error:", error);
      return false;
    }
  };

  // --- ✅ Wishlist Logic ---
  useEffect(() => {
    if (user?.wishlist) {
      setWishlistSet(new Set(user.wishlist));
      setWishlistCount(user.wishlist.length); // Update wishlist count when the user loads
    } else {
      setWishlistSet(new Set());
      setWishlistCount(0); // If no wishlist, set count to 0
    }
  }, [user?.wishlist]);

  const addToWishlist = (productId: number) => {
    const newSet = new Set(wishlistSet);
    newSet.add(productId);
    setWishlistSet(newSet);

    if (user) {
      setUser({
        ...user,
        wishlist: Array.from(newSet),
        wishlistItemCount: newSet.size,
      });
    }
  };

  const removeFromWishlist = (productId: number) => {
    const newSet = new Set(wishlistSet);
    newSet.delete(productId);
    setWishlistSet(newSet);

    if (user) {
      setUser({
        ...user,
        wishlist: Array.from(newSet),
        wishlistItemCount: newSet.size,
      });
      setWishlistCount(newSet.size); // Update wishlist count after removing item
    }
  };

  const isInWishlist = (productId: number): boolean =>
    wishlistSet.has(productId);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        login,
        signup,
        logout,
        updateProfile,
        refreshUser,
        wishlistSet,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        toggleWishlist,
        showAuthModal,
        setShowAuthModal,
        scrollToProducts,
        productListingRef,
        wishlistCount,
        setWishlistCount,
        // isWishlisted is now managed internally by toggleWishlist
        isWishlisted,
        setIsWishlisted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
